import React from 'react';
import { motion } from 'framer-motion';
import { ChefHat, Clock, Utensils, Sparkles, Info, Gauge, BookOpen } from 'lucide-react';
import { RecipeSection } from './RecipeSection';
import { GlowEffect } from '../../effects/GlowEffect';

interface RecipeStepsProps {
  steps: string;
  useThermomix?: boolean;
}

const getStepIcon = (line: string, useThermomix: boolean = false) => {
  const lowerLine = line.toLowerCase();
  if (lowerLine.includes('preparación')) return Clock;
  if (lowerLine.includes('thermomix') || lowerLine.includes('elaboración')) {
    return useThermomix ? Gauge : Utensils;
  }
  if (lowerLine.includes('emplatado')) return Sparkles;
  if (lowerLine.includes('nutricional')) return Info;
  if (lowerLine.includes('alternativas')) return BookOpen;
  return ChefHat;
};

export const RecipeSteps: React.FC<RecipeStepsProps> = ({ steps, useThermomix = false }) => {
  const sections = steps.split('\n').reduce((acc, line) => {
    const trimmedLine = line.trim();
    if (!trimmedLine) return acc;

    if (trimmedLine.match(/^#+\s/)) {
      acc.push({ title: trimmedLine.replace(/^#+\s/, ''), content: [] });
    } else if (acc.length > 0) {
      acc[acc.length - 1].content.push(trimmedLine);
    }
    return acc;
  }, [] as { title: string; content: string[] }[]);

  return (
    <GlowEffect>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg p-6 shadow-lg mt-6"
      >
        <div className="flex items-center gap-3 mb-6">
          <ChefHat className="text-orange-500" size={24} />
          <h3 className="text-xl font-bold">Pasos de preparación</h3>
        </div>

        <div className="space-y-8">
          {sections.map((section, index) => {
            const Icon = getStepIcon(section.title, useThermomix);
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.2 }}
                className="bg-orange-50/50 rounded-lg p-6 hover:shadow-md transition-all"
              >
                <h4 className="flex items-center gap-2 text-lg font-semibold text-orange-800 mb-4">
                  <Icon size={20} className="text-orange-500" />
                  {section.title}
                </h4>
                <div className="space-y-3 prose prose-orange max-w-none">
                  {section.content.map((line, i) => (
                    <RecipeSection
                      key={i}
                      line={line}
                      isMainSection={line.match(/^\d+\./)}
                      useThermomix={useThermomix}
                    />
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </GlowEffect>
  );
};